import {useCallback, useEffect, useMemo, useState} from "react";
import {useMutation, useQueryClient} from "react-query";
import Alert from "react-s-alert";

import {
  TableUserDto,
  UserPermissionEnum,
} from "../../../../../utils/types/user";
import {UserService} from "../../../../../services/UserService";
import {buildResponseErrorMessage} from "../../../../../services/api/errorHelpers";
import {
  applyBulkCategoryToggle,
  applyBulkPermissionToggle,
  countBulkPermissionChanges,
  countBulkPendingPermissionChanges,
  isBulkPermissionPending,
  permissionsByUserIdFromUsers,
  PermissionsByUserId,
} from "../bulkPermissions.helpers";
import {
  applyQuicksightReportingSync,
  DashboardIdsByEmail,
} from "../bulkQuicksightDashboards.helpers";

export const useBulkUserPermissionsMutation = (
  users: ReadonlyArray<TableUserDto>,
) => {
  const queryClient = useQueryClient();
  const userIds = useMemo(
    () =>
      users
        .map(user => user.id)
        .filter((userId): userId is string => Boolean(userId)),
    [users],
  );

  const [savedPermissionsByUserId, setSavedPermissionsByUserId] =
    useState<PermissionsByUserId>({});
  const [draftPermissionsByUserId, setDraftPermissionsByUserId] =
    useState<PermissionsByUserId>({});

  useEffect(() => {
    const initialPermissions = permissionsByUserIdFromUsers(users);
    setSavedPermissionsByUserId(initialPermissions);
    setDraftPermissionsByUserId(initialPermissions);
  }, [users]);

  const {mutate, isLoading: isSaving} = useMutation(
    async (nextPermissionsByUserId: PermissionsByUserId) => {
      const changedUserIds = userIds.filter(userId => {
        const saved = savedPermissionsByUserId[userId] ?? [];
        const draft = nextPermissionsByUserId[userId] ?? [];

        return (
          countBulkPermissionChanges({[userId]: saved}, {[userId]: draft}, [
            userId,
          ]) > 0
        );
      });

      await UserService.bulkPutReportingPermissions(
        changedUserIds.map(userId => ({
          userId,
          permissions: nextPermissionsByUserId[userId] ?? [],
        })),
      );

      return {changedUserIds, permissionsByUserId: nextPermissionsByUserId};
    },
    {
      onSuccess: ({changedUserIds, permissionsByUserId}) => {
        setSavedPermissionsByUserId(currentSavedPermissionsByUserId => {
          const nextSavedPermissionsByUserId = {
            ...currentSavedPermissionsByUserId,
          };

          changedUserIds.forEach(userId => {
            nextSavedPermissionsByUserId[userId] =
              permissionsByUserId[userId] ?? [];
          });

          return nextSavedPermissionsByUserId;
        });
        setDraftPermissionsByUserId(currentDraftPermissionsByUserId => {
          const nextDraftPermissionsByUserId = {
            ...currentDraftPermissionsByUserId,
          };

          userIds.forEach(userId => {
            nextDraftPermissionsByUserId[userId] =
              permissionsByUserId[userId] ?? [];
          });

          return nextDraftPermissionsByUserId;
        });
        queryClient.invalidateQueries("usersPermissionQuery");
        changedUserIds.forEach(userId => {
          queryClient.invalidateQueries(["useUserQuery", userId]);
        });
      },
      onError: async (resp: unknown) => {
        Alert.warning(
          await buildResponseErrorMessage(
            resp,
            "Failed to save permissions for selected users",
          ),
        );
      },
    },
  );

  const hasPermission = useCallback(
    (userId: string, permission: UserPermissionEnum) =>
      (draftPermissionsByUserId[userId] ?? []).includes(permission),
    [draftPermissionsByUserId],
  );

  const isPermissionPending = useCallback(
    (permission: UserPermissionEnum) =>
      isBulkPermissionPending(
        savedPermissionsByUserId,
        draftPermissionsByUserId,
        userIds,
        permission,
      ),
    [draftPermissionsByUserId, savedPermissionsByUserId, userIds],
  );

  const togglePermission = useCallback(
    (permission: UserPermissionEnum, grantToAll: boolean) => {
      setDraftPermissionsByUserId(currentPermissionsByUserId =>
        applyBulkPermissionToggle(
          currentPermissionsByUserId,
          userIds,
          permission,
          grantToAll,
        ),
      );
    },
    [userIds],
  );

  const toggleCategory = useCallback(
    (permissions: ReadonlyArray<UserPermissionEnum>, grantToAll: boolean) => {
      setDraftPermissionsByUserId(currentPermissionsByUserId =>
        applyBulkCategoryToggle(
          currentPermissionsByUserId,
          userIds,
          permissions,
          grantToAll,
        ),
      );
    },
    [userIds],
  );

  const pendingChangeCount = useMemo(
    () =>
      countBulkPendingPermissionChanges(
        savedPermissionsByUserId,
        draftPermissionsByUserId,
        userIds,
      ),
    [draftPermissionsByUserId, savedPermissionsByUserId, userIds],
  );

  const discardChanges = useCallback(() => {
    setDraftPermissionsByUserId(savedPermissionsByUserId);
  }, [savedPermissionsByUserId]);

  const syncQuicksightReporting = useCallback(
    (dashboardsByEmail: DashboardIdsByEmail) => {
      setDraftPermissionsByUserId(currentPermissionsByUserId =>
        applyQuicksightReportingSync(
          currentPermissionsByUserId,
          users,
          dashboardsByEmail,
        ),
      );
    },
    [users],
  );

  const reconcileDraftPermissions = useCallback(
    (permissionsByUserId: PermissionsByUserId) => {
      setDraftPermissionsByUserId(currentDraftPermissionsByUserId => {
        let hasChanges = false;
        const nextDraftPermissionsByUserId = {
          ...currentDraftPermissionsByUserId,
        };

        userIds.forEach(userId => {
          const nextPermissions = permissionsByUserId[userId] ?? [];
          const currentPermissions = currentDraftPermissionsByUserId[userId] ?? [];

          const permissionsChanged =
            nextPermissions.length !== currentPermissions.length ||
            nextPermissions.some(
              (permission, index) => permission !== currentPermissions[index],
            );

          if (permissionsChanged) {
            hasChanges = true;
            nextDraftPermissionsByUserId[userId] = nextPermissions;
          }
        });

        return hasChanges
          ? nextDraftPermissionsByUserId
          : currentDraftPermissionsByUserId;
      });
    },
    [userIds],
  );

  const save = useCallback(
    (
      options?: Readonly<{
        permissionsByUserId?: PermissionsByUserId;
        onSuccess?: () => void;
      }>,
    ) => {
      const nextPermissionsByUserId =
        options?.permissionsByUserId ?? draftPermissionsByUserId;

      if (options?.permissionsByUserId) {
        reconcileDraftPermissions(nextPermissionsByUserId);
      }

      const hasPermissionPayloadChanges =
        countBulkPermissionChanges(
          savedPermissionsByUserId,
          nextPermissionsByUserId,
          userIds,
        ) > 0;

      if (!hasPermissionPayloadChanges) {
        options?.onSuccess?.();
        return;
      }

      mutate(nextPermissionsByUserId, {
        onSuccess: () => {
          options?.onSuccess?.();
        },
      });
    },
    [
      draftPermissionsByUserId,
      mutate,
      reconcileDraftPermissions,
      savedPermissionsByUserId,
      userIds,
    ],
  );

  return {
    discardChanges,
    draftPermissionsByUserId,
    hasPermission,
    isPermissionPending,
    isSaving,
    pendingChangeCount,
    save,
    syncQuicksightReporting,
    toggleCategory,
    togglePermission,
    userIds,
  };
};
